<template>
  <div class="content">
    <div class="course-form-wrap" v-loading.fullscreen.lock="saving">
      <card class="card no-border-card" no-body>
        <div class="cf">

          <!-- ============ TOP HEADER ============ -->
          <header class="cf-top">
            <div class="cf-top__left">
              <nav class="cf-crumbs">
                <a class="cf-crumb" @click="$router.push('/glad/courses')">← Courses</a>
                <span class="cf-crumb-sep">/</span>
                <span class="cf-crumb cf-crumb--current">{{ isEdit ? 'Edit course' : 'New course' }}</span>
              </nav>

              <div class="cf-title-row">
                <input
                  v-model="form.name"
                  class="cf-title-input"
                  placeholder="Untitled course"
                  maxlength="160" />
                <span class="cf-status" :class="'cf-status--' + (form.status === 1 ? 'published' : 'draft')">
                  {{ form.status === 1 ? 'Published' : 'Draft' }}
                </span>
              </div>

              <p class="cf-subtitle">Build the lessons and content learners will move through.</p>
            </div>

            <div class="cf-top__stats">
              <div class="cf-stat">
                <div class="cf-stat__value" :class="{ 'cf-stat__value--muted': form.lessons.length === 0 }">
                  {{ form.lessons.length || '—' }}
                </div>
                <div class="cf-stat__label">Lessons</div>
              </div>
              <div class="cf-stat">
                <div class="cf-stat__value">{{ form.allow_attempts }}</div>
                <div class="cf-stat__label">Max attempts</div>
              </div>
            </div>

            <div class="cf-top__actions">
              <button type="button" class="cf-btn cf-btn--ghost" @click="$router.push('/glad/courses')">Cancel</button>
              <button type="button" class="cf-btn cf-btn--primary" :disabled="saving" @click="submit">
                {{ saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create course') }}
              </button>
            </div>
          </header>

          <!-- ============ BODY ============ -->
          <div class="cf-body">
            <div v-if="loadingCourse" class="cf-loading">
              <span class="cf-spinner" aria-label="Loading course"></span>
            </div>

            <form v-else @submit.prevent="submit" class="cf-form">
              <!-- ── Course details ─────────────────────────── -->
              <section class="cf-card">
                <div class="cf-card__head">
                  <h2 class="cf-card__title">Course details</h2>
                </div>

                <div class="cf-field">
                  <label class="cf-label" for="c-desc">Description <span class="cf-req">*</span></label>
                  <textarea id="c-desc" v-model="form.description" class="cf-input cf-input--textarea"
                    rows="4" placeholder="What will learners understand and be able to do?" required></textarea>
                </div>

                <div class="cf-field-row">
                  <div class="cf-field">
                    <label class="cf-label" for="c-attempts">Max attempts <span class="cf-req">*</span></label>
                    <input id="c-attempts" v-model.number="form.allow_attempts" type="number" min="1" max="99"
                      class="cf-input cf-input--short" required />
                  </div>
                  <div class="cf-field">
                    <label class="cf-label" for="c-status">Status</label>
                    <div class="cf-select-wrap">
                      <select id="c-status" v-model="form.status" class="cf-select">
                        <option :value="1">Published</option>
                        <option :value="0">Draft</option>
                      </select>
                      <svg class="cf-select__chev" viewBox="0 0 20 20" fill="currentColor" width="11" height="11" aria-hidden="true"><path fill-rule="evenodd" d="M5.22 8.22a.75.75 0 011.06 0L10 11.94l3.72-3.72a.75.75 0 111.06 1.06l-4.25 4.25a.75.75 0 01-1.06 0L5.22 9.28a.75.75 0 010-1.06z" clip-rule="evenodd"/></svg>
                    </div>
                  </div>
                </div>
              </section>

              <!-- ── Lessons ────────────────────────────────── -->
              <section class="cf-card">
                <div class="cf-card__head">
                  <h2 class="cf-card__title">Lessons</h2>
                  <button type="button" class="cf-btn cf-btn--ghost cf-btn--sm" @click="addLesson">+ Add lesson</button>
                </div>

                <p v-if="form.lessons.length === 0" class="cf-empty">
                  No lessons yet — click <strong>+ Add lesson</strong> to start building content.
                </p>

                <div
                  v-for="(lesson, idx) in form.lessons"
                  :key="idx"
                  class="cf-lesson"
                >
                  <div class="cf-lesson__head">
                    <span class="cf-lesson__num">Lesson {{ idx + 1 }}</span>
                    <button type="button" class="cf-lesson__remove" @click="removeLesson(idx)" title="Remove lesson" aria-label="Remove lesson">
                      <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14"><path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z"/></svg>
                    </button>
                  </div>

                  <div class="cf-field">
                    <label class="cf-label">Lesson title <span class="cf-req">*</span></label>
                    <input v-model="lesson.name" class="cf-input" placeholder="e.g. Academic Language Strategies" required />
                  </div>

                  <div class="cf-field">
                    <label class="cf-label">Content type</label>
                    <div class="cf-type-tabs">
                      <button v-for="t in lessonTypes" :key="t.value" type="button"
                        class="cf-type-tab"
                        :class="{ 'cf-type-tab--active': lesson.type === t.value }"
                        @click="lesson.type = t.value">
                        {{ t.label }}
                      </button>
                    </div>
                  </div>

                  <!-- Text content -->
                  <div v-if="lesson.type === 'text'" class="cf-field">
                    <label class="cf-label">Content</label>
                    <textarea v-model="lesson.content" class="cf-input cf-input--textarea" rows="5"
                      placeholder="Lesson content (supports HTML)"></textarea>
                  </div>

                  <!-- YouTube video -->
                  <div v-if="lesson.type === 'youtube-video'" class="cf-field">
                    <label class="cf-label">YouTube video ID or URL</label>
                    <input v-model="lesson.video_url" class="cf-input"
                      placeholder="e.g. dQw4w9WgXcQ or https://youtube.com/watch?v=..." />
                    <span class="cf-hint">Enter a YouTube video ID or full URL. It will be embedded securely.</span>
                  </div>

                  <!-- Vimeo video -->
                  <div v-if="lesson.type === 'video'" class="cf-field">
                    <label class="cf-label">Vimeo video ID</label>
                    <input v-model="lesson.video_url" class="cf-input" placeholder="e.g. 123456789" />
                    <span class="cf-hint">Enter the numeric Vimeo video ID only.</span>
                  </div>

                  <!-- PDF URL -->
                  <div v-if="lesson.type === 'pdf'" class="cf-field">
                    <label class="cf-label">PDF URL</label>
                    <input v-model="lesson.pdf_url" class="cf-input" type="url"
                      placeholder="https://example.com/document.pdf" />
                    <span class="cf-hint">Direct link to a publicly accessible PDF.</span>
                  </div>
                </div>
              </section>

              <!-- ── Error / Footer actions ─────────────────── -->
              <p v-if="error" class="cf-alert" role="alert">{{ error }}</p>

              <div class="cf-footer-actions">
                <button type="button" class="cf-btn cf-btn--ghost" @click="$router.push('/glad/courses')">Cancel</button>
                <button type="submit" class="cf-btn cf-btn--primary" :disabled="saving">
                  {{ saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create course') }}
                </button>
              </div>
            </form>
          </div>

        </div>
      </card>
    </div>
  </div>
</template>

<script>
export default {
  name: 'GladCourseForm',
  data() {
    return {
      loadingCourse: false,
      saving: false,
      error: '',
      form: {
        name: '',
        description: '',
        allow_attempts: 3,
        status: 1,
        lessons: [],
      },
      lessonTypes: [
        { value: 'text',          label: 'Text / HTML' },
        { value: 'youtube-video', label: 'YouTube' },
        { value: 'video',         label: 'Vimeo' },
        { value: 'pdf',           label: 'PDF link' },
      ],
    };
  },
  computed: {
    isEdit() { return !!this.$route.params.id; },
    courseId() { return this.$route.params.id; },
  },
  created() {
    if (this.isEdit) this.loadCourse();
  },
  methods: {
    // ── Load existing course ──────────────────────────────────
    loadCourse() {
      this.loadingCourse = true;
      this.$http.get('course/edit/' + this.courseId)
        .then(resp => {
          const raw = Array.isArray(resp.data) ? resp.data[0] : resp.data;
          this.form.name         = raw.name || '';
          this.form.description  = raw.description || '';
          this.form.allow_attempts = raw.allow_attempts || 3;
          this.form.status       = raw.status != null ? parseInt(raw.status) : 1;
          // Map existing lessons
          this.form.lessons = (raw.course_lessons || []).map(l => ({
            id:        l.id,
            name:      l.course_lesson_name || '',
            type:      l.type || 'text',
            content:   l.course_lesson_content || '',
            video_url: this.stripEmbedPrefix(l.course_lesson_video || ''),
            pdf_url:   l.course_lesson_pdf || '',
          }));
        })
        .catch(() => { this.error = 'Failed to load course. Please go back and try again.'; })
        .finally(() => { this.loadingCourse = false; });
    },

    stripEmbedPrefix(url) {
      return url
        .replace('https://player.vimeo.com/video/', '')
        .replace('https://www.youtube.com/embed/', '')
        .replace('https://youtube.com/embed/', '');
    },

    extractYoutubeId(input) {
      if (!input) return '';
      // Already an ID (no slash or dot)
      if (/^[a-zA-Z0-9_-]{11}$/.test(input.trim())) return input.trim();
      // URL forms: ?v=, /embed/, youtu.be/
      const m = input.match(/(?:v=|embed\/|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
      return m ? m[1] : input.trim();
    },

    // ── Lesson helpers ────────────────────────────────────────
    addLesson() {
      this.form.lessons.push({ id: null, name: '', type: 'text', content: '', video_url: '', pdf_url: '' });
    },
    removeLesson(idx) {
      this.form.lessons.splice(idx, 1);
    },

    // ── Build FormData ────────────────────────────────────────
    buildFormData() {
      const fd = new FormData();
      fd.append('course_name',          this.form.name);
      fd.append('course_description',   this.form.description);
      fd.append('course_allow_attempts', this.form.allow_attempts);
      fd.append('course_status',        this.form.status);
      fd.append('course_in_store',      this.form.status); // publish to store = same as status
      fd.append('foremployee',          1);
      fd.append('formanager',           0);
      fd.append('isScorm',              0);

      this.form.lessons.forEach((lesson, i) => {
        fd.append(`lessons[${i}][course_lesson_name]`,              lesson.name);
        fd.append(`lessons[${i}][type]`,                           lesson.type);
        fd.append(`lessons[${i}][course_lesson_content]`,          lesson.content || '');
        fd.append(`lessons[${i}][course_next_button_timer_status]`, 0);
        fd.append(`lessons[${i}][course_timer_value]`,             '00:00:00');
        fd.append(`lessons[${i}][course_lesson_quiz]`,             '');
        fd.append(`lessons[${i}][quizStatus]`,                     0);
        fd.append(`lessons[${i}][allowed_attempts]`,               3);
        fd.append(`lessons[${i}][passing_rate]`,                   70);
        fd.append(`lessons[${i}][no_of_questions]`,                0);
        fd.append(`lessons[${i}][course_passmessage]`,             '');

        if (lesson.type === 'youtube-video') {
          const ytId = this.extractYoutubeId(lesson.video_url);
          fd.append(`lessons[${i}][course_lesson_video]`, `https://www.youtube.com/embed/${ytId}`);
        } else if (lesson.type === 'video') {
          fd.append(`lessons[${i}][course_lesson_video]`, `https://player.vimeo.com/video/${lesson.video_url}`);
        } else if (lesson.type === 'pdf') {
          fd.append(`lessons[${i}][course_lesson_pdf]`, lesson.pdf_url);
        }
      });

      return fd;
    },

    // ── Submit ────────────────────────────────────────────────
    submit() {
      this.error = '';
      if (!this.form.name.trim()) { this.error = 'Course title is required.'; return; }
      if (!this.form.description.trim()) { this.error = 'Description is required.'; return; }
      if (this.form.lessons.length === 0) { this.error = 'Add at least one lesson before saving.'; return; }
      const emptyName = this.form.lessons.find(l => !l.name.trim());
      if (emptyName) { this.error = 'Each lesson must have a title.'; return; }

      this.saving = true;
      const fd = this.buildFormData();
      const config = { headers: { 'Content-Type': 'multipart/form-data' } };

      const req = this.isEdit
        ? this.$http.post('course/update/' + this.courseId, fd, config) // HOT uses POST for updates too
        : this.$http.post('course', fd, config);

      req
        .then(() => {
          this.$toast && this.$toast.success(this.isEdit ? 'Course updated.' : 'Course created.', { timeout: 3000 });
          this.$router.push('/glad/courses');
        })
        .catch(err => {
          const msg = err.response?.data?.message || 'Something went wrong. Please try again.';
          this.error = msg;
        })
        .finally(() => { this.saving = false; });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

/* ============================================================
   GladCourseForm — mirrors the PathwayBuilder design language:
   surface (#fff) workspace card, paper body, ink/amber tokens,
   serif inline title, status pill, stat chips, pb-style buttons.
   ============================================================ */

.course-form-wrap { width: 100%; }
.course-form-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
  background: transparent;
}

.cf {
  /* Local CSS-var fallbacks — scoped :root rewrites lose these otherwise */
  --glad-ink:         #{$glad-ink};
  --glad-ink-2:       #{$glad-ink-2};
  --glad-paper:       #{$glad-paper};
  --glad-paper-2:     #{$glad-paper-2};
  --glad-surface:     #{$glad-surface};
  --glad-line:        #{$glad-line};
  --glad-line-strong: #{$glad-line-strong};
  --glad-text:        #{$glad-text};
  --glad-text-soft:   #{$glad-text-soft};
  --glad-text-muted:  #{$glad-text-muted};
  --glad-accent:      #{$glad-accent};
  --glad-success:     #{$glad-success};
  --glad-danger:      #{$glad-danger};
  --glad-font-sans:   #{$glad-font-sans};
  --glad-font-serif:  #{$glad-font-serif};

  font-family: var(--glad-font-sans);
  color: var(--glad-text);
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 12px;
  overflow: hidden;
}

/* ---------- TOP HEADER ---------- */
.cf-top {
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: 1.5rem;
  align-items: end;
  padding: 1.1rem 1.25rem;
  border-bottom: 1px solid var(--glad-line);
  background: var(--glad-surface);
}
@media (max-width: 860px) {
  .cf-top { grid-template-columns: 1fr; gap: 1rem; align-items: stretch; }
}

.cf-top__left { min-width: 0; display: flex; flex-direction: column; gap: 0.35rem; }

.cf-crumbs {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.78rem;
  color: var(--glad-text-soft);
}
.cf-crumb {
  color: var(--glad-text-soft);
  cursor: pointer;
  text-decoration: none;
  transition: color 0.12s ease;
  &:hover { color: var(--glad-ink); }
}
.cf-crumb--current { color: var(--glad-ink); font-weight: 500; cursor: default; }
.cf-crumb-sep { color: var(--glad-line-strong); }

.cf-title-row {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  min-width: 0;
}
.cf-title-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  outline: none;
  padding: 0;
  font-family: var(--glad-font-serif);
  font-size: 1.625rem;
  font-weight: 600;
  letter-spacing: -0.025em;
  line-height: 1.15;
  color: var(--glad-ink);
  &::placeholder { color: var(--glad-text-muted); opacity: 1; font-weight: 500; }
  &:focus { box-shadow: inset 0 -2px 0 0 var(--glad-line); }
}
.cf-status {
  display: inline-flex;
  align-items: center;
  font-size: 0.7rem;
  font-weight: 500;
  padding: 0.18rem 0.55rem;
  border-radius: 999px;
  flex-shrink: 0;
  &--draft     { background: var(--glad-paper-2); color: var(--glad-text-soft); }
  &--published { background: #dcfce7; color: #166534; }
}
.cf-subtitle {
  font-size: 0.875rem;
  color: var(--glad-text-soft);
  margin: 0.1rem 0 0;
}

.cf-top__stats {
  display: flex;
  gap: 1.25rem;
  align-items: flex-end;
  align-self: flex-end;
}
.cf-stat { display: flex; flex-direction: column; align-items: flex-start; min-width: 56px; }
.cf-stat__value {
  font-family: var(--glad-font-sans);
  font-size: 1.375rem;
  font-weight: 600;
  color: var(--glad-ink);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
}
.cf-stat__value--muted { color: var(--glad-text-muted); font-weight: 500; }
.cf-stat__label {
  font-size: 0.72rem;
  font-weight: 500;
  color: var(--glad-text-soft);
  margin-top: 0.15rem;
}

.cf-top__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-end;
}

/* ---------- BODY ---------- */
.cf-body {
  background: var(--glad-paper);
  padding: 1.5rem 1.25rem 2rem;
}
.cf-form {
  max-width: 760px;
  margin: 0 auto;
}

/* ---------- Cards ---------- */
.cf-card {
  background: var(--glad-surface);
  border: 1px solid var(--glad-line);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.25rem;
}
.cf-card__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.6rem;
  margin-bottom: 1.25rem;
}
.cf-card__title {
  font-family: var(--glad-font-serif);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0;
}

/* ---------- Fields ---------- */
.cf-field { margin-bottom: 1.1rem; &:last-child { margin-bottom: 0; } }
.cf-field-row { display: flex; gap: 1rem; flex-wrap: wrap; }
.cf-field-row .cf-field { flex: 1; min-width: 150px; margin-bottom: 0; }
.cf-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--glad-ink);
  margin-bottom: 0.4rem;
}
.cf-req { color: var(--glad-accent); }
.cf-hint {
  font-size: 0.78rem;
  color: var(--glad-text-muted);
  margin-top: 0.35rem;
  display: block;
}

.cf-input {
  width: 100%;
  box-sizing: border-box;
  padding: 0.55rem 0.75rem;
  border: 1px solid var(--glad-line);
  border-radius: 8px;
  font-family: var(--glad-font-sans);
  font-size: 0.9rem;
  color: var(--glad-ink);
  background: var(--glad-surface);
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  &::placeholder { color: var(--glad-text-muted); }
  &:hover { border-color: var(--glad-line-strong); }
  &:focus {
    outline: none;
    border-color: var(--glad-ink);
    box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.08);
  }
  &--textarea { resize: vertical; min-height: 96px; line-height: 1.5; }
  &--short { max-width: 130px; }
}

.cf-select-wrap { position: relative; display: block; }
.cf-select {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  box-sizing: border-box;
  padding: 0.55rem 1.75rem 0.55rem 0.75rem;
  border: 1px solid var(--glad-line);
  border-radius: 8px;
  font-family: var(--glad-font-sans);
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--glad-ink);
  background: var(--glad-surface);
  cursor: pointer;
  transition: border-color 0.12s ease, box-shadow 0.12s ease;
  &:hover { border-color: var(--glad-line-strong); }
  &:focus {
    outline: none;
    border-color: var(--glad-ink);
    box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.08);
  }
}
.cf-select__chev {
  position: absolute;
  right: 0.65rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: var(--glad-text-soft);
}

/* ---------- Lessons ---------- */
.cf-lesson {
  border: 1px solid var(--glad-line);
  border-radius: 10px;
  padding: 1.15rem;
  margin-bottom: 0.85rem;
  background: var(--glad-paper);
  &:last-child { margin-bottom: 0; }
}
.cf-lesson__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.9rem;
}
.cf-lesson__num {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--glad-accent);
}
.cf-lesson__remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--glad-text-muted);
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  &:hover { background: #fef2f2; color: var(--glad-danger); }
}

/* ---------- Content-type segmented tabs ---------- */
.cf-type-tabs { display: flex; gap: 0.4rem; flex-wrap: wrap; }
.cf-type-tab {
  padding: 0.38rem 0.8rem;
  border-radius: 7px;
  border: 1px solid var(--glad-line);
  background: var(--glad-surface);
  color: var(--glad-ink);
  font-family: var(--glad-font-sans);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
  &--active {
    background: var(--glad-ink);
    border-color: var(--glad-ink);
    color: #fff;
  }
  &:hover:not(.cf-type-tab--active) { border-color: var(--glad-line-strong); }
}

/* ---------- Buttons (pb-btn parity) ---------- */
.cf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 34px;
  flex-shrink: 0;
  padding: 0 0.95rem;
  border-radius: 8px;
  font-family: var(--glad-font-sans);
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  line-height: 1;
  letter-spacing: -0.005em;
  border: 1px solid var(--glad-ink);
  background: var(--glad-ink);
  color: #fff;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;
  &:focus-visible { outline: none; box-shadow: 0 0 0 3px rgba(15, 17, 21, 0.18); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  &--primary {
    background: var(--glad-ink);
    color: #fff;
    border-color: var(--glad-ink);
    &:hover:not(:disabled) { background: var(--glad-ink-2); border-color: var(--glad-ink-2); }
  }
  &--ghost {
    background: var(--glad-surface);
    color: var(--glad-ink);
    border-color: var(--glad-line);
    &:hover:not(:disabled) { background: var(--glad-paper); border-color: var(--glad-line-strong); }
  }
  &--sm { height: 28px; padding: 0 0.7rem; font-size: 0.76rem; }
}

.cf-footer-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  max-width: 760px;
  margin: 0 auto;
}

/* ---------- Misc ---------- */
.cf-empty {
  color: var(--glad-text-soft);
  font-size: 0.9rem;
  margin: 0;
  strong { color: var(--glad-ink); }
}
.cf-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  border-radius: 8px;
  padding: 0.7rem 0.95rem;
  margin: 0 auto 1rem;
  max-width: 760px;
  font-size: 0.86rem;
}
.cf-loading { text-align: center; padding: 3.5rem 0; }
.cf-spinner {
  display: inline-block;
  width: 32px;
  height: 32px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: cf-spin 0.7s linear infinite;
}
@keyframes cf-spin { to { transform: rotate(360deg); } }
</style>
